import { ApplicationRef, Injectable, inject } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { concat, first, interval, ReplaySubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SwUpdateService {
  private appRef = inject(ApplicationRef);

  private swUpdate = inject(SwUpdate);

  updateFound$ = new ReplaySubject<boolean>(1);

  private isUpdateReadyState = false;

  init() {
    if (this.swUpdate.isEnabled) {
      this.pollForUpdates();
      this.subscribeToVersionUpdates();
      this.subscribeUnrecoverableState();
    }
  }

  checkForUpdate() {
    if (!this.swUpdate.isEnabled) {
      return Promise.resolve(false);
    }
    return this.swUpdate.checkForUpdate();
  }

  /**
   * Check if there's an update already available and ready to be activated
   * @returns boolean - true if there's an update ready to be activated
   */
  isUpdateReady(): boolean {
    if (!this.swUpdate.isEnabled) {
      return false;
    }
    // Return the current state directly instead of waiting for new events
    return this.isUpdateReadyState;
  }

  private subscribeToVersionUpdates() {
    this.swUpdate.versionUpdates.subscribe((event) => {
      if (event.type === 'VERSION_READY') {
        this.isUpdateReadyState = true;
      } else if (event.type === 'VERSION_DETECTED') {
        // Reset the state when a new version is detected (not yet ready)
        this.isUpdateReadyState = false;
      }
    });
  }

  private pollForUpdates() {
    // Allow the app to stabilize first, before starting
    // polling for updates with `interval()`.
    const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));
    const everySixHours$ = interval(6 * 60 * 60 * 1000);
    const everySixHoursOnceAppIsStable$ = concat(appIsStable$, everySixHours$);
    everySixHoursOnceAppIsStable$.subscribe(async () => {
      try {
        const updateFound = await this.swUpdate.checkForUpdate();
        console.log(updateFound ? 'A new version is available.' : 'Already on the latest version.');
        this.updateFound$.next(updateFound);
      } catch (err) {
        console.error('Failed to check for updates:', err);
      }
    });
  }

  private subscribeUnrecoverableState() {
    this.swUpdate.unrecoverable.subscribe(() => {
      document.location.reload();
    });
  }
}
